import { Checkbox, Collapse } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, BookSaved } from '../../assets/icons'
import { Card, CenteredSpin, IconButtonSmall, Tag } from '../ui'
import {
    ButtonContainer,
    CardPanel,
    DocumentsGrid,
    HistoryModal,
    individualEntrepreneurInfoConfig,
    legalPersonInfoConfig,
    privatePersonInfoConfig,
    Questionnaire,
    Sign,
} from './components/'
import request from '../../services/request'

import style from './style.m.less'
import { EStatus, statusMap } from '../../constants/approval'
import { IUserInfo } from 'store/user/interfaces'
import { EDocumentType } from '../../constants/profile'
import { IAttorneyData } from '../../store/extension'
import { AttorneyBlock } from './components'
import { EUserType } from '../../constants/approval'
import {useCurrentUser} from "../hooks/useCurrentUser";

const getConfig = (profile: IUserInfo, nextProfile?: IUserInfo) => {
    if (profile.user_type === EUserType.FizLico) {
        return privatePersonInfoConfig(profile, nextProfile)
    } else if (profile.user_type === EUserType.IP) {
        return individualEntrepreneurInfoConfig(profile, nextProfile)
    } else {
        return legalPersonInfoConfig(profile, nextProfile)
    }
}

export enum EAction {
    SignUp = 'Регистрация',
    ChangeRole = 'СменаРоли',
    Edit = 'Редактирование',
}

const QuestionnaireApprovalItem: FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id, uid, prevId } = useParams<{ id: string; uid: string; prevId: string }>()
    const { isUserAdmin } = useCurrentUser()

    const [isHistoryVisible, setIsHistoryVisible] = useState(false)
    const openHistory = () => setIsHistoryVisible(true)
    const closeHistory = () => setIsHistoryVisible(false)

    const [loading, setLoading] = useState(true)
    const [profile, setProfile] = useState<IUserInfo>()
    const [prevProfile, setPrevProfile] = useState<IUserInfo>()
    const [history, setHistory] = useState()
    const [attorney, setAttorney] = useState<IAttorneyData | null>(null)
    const [prevAttorney, setPrevAttorney] = useState<IAttorneyData | null>(null)

    useEffect(() => {
        const loadHistory = async () => {
            const res = await request.get(`/api/users/v1/profiles-events?uid=${uid}`)
            setHistory(res.data)
        }
        loadHistory()
    }, [uid])

    useEffect(() => {
        const loadProfile = async () => {
            const res = await request.get(`/api/users/v1/profile-admin?profile_id=${id}&uid=${uid}`)
            setProfile(res.data)
            setAttorney(res.data.machine_readable_attorney)
        }
        const loadPrevProfile = async () => {
            const res = await request.get(
                `/api/users/v1/profile-admin?profile_id=${prevId}&uid=${uid}`,
            )
            setPrevProfile(res.data)
            setPrevAttorney(res.data.machine_readable_attorney)
        }
        const load = async () => {
            setLoading(true)
            try {
                const promises = [loadProfile()]
                if (prevId && +prevId) {
                    promises.push(loadPrevProfile())
                } else {
                    setPrevProfile(undefined)
                }
                await Promise.all(promises)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [id, prevId, uid])

    const action =
        !prevId || prevProfile?.profile_status === 0
            ? EAction.SignUp
            : profile?.user_role !== prevProfile?.user_role
            ? EAction.ChangeRole
            : EAction.Edit

    const showPrevProfile = prevProfile?.profile_status !== 0

    const prevProfileDocuments = []
    const prevProfileDocumentsContractMaintainRegister = []
    const prevProfileDocumentsMachineReadableAttorney = []
    for (let i = 0; i < prevProfile?.documents?.length; i++) {
        if (prevProfile?.documents[i].document_type === EDocumentType.ContractMaintainRegister) {
            prevProfileDocumentsContractMaintainRegister.push(prevProfile?.documents[i])
        } else if (prevProfile?.documents[i].document_type === EDocumentType.MachineReadableAttorney) {
            prevProfileDocumentsMachineReadableAttorney.push(prevProfile?.documents[i])
        } else {
            prevProfileDocuments.push(prevProfile?.documents[i])
        }
    }

    const profileDocuments = []
    const profileDocumentsContractMaintainRegister = []
    const profileDocumentsMachineReadableAttorney = []
    for (let i = 0; i < profile?.documents?.length; i++) {
        if (profile?.documents[i].document_type === EDocumentType.ContractMaintainRegister) {
            profileDocumentsContractMaintainRegister.push(profile?.documents[i])
        } else if (profile?.documents[i].document_type === EDocumentType.MachineReadableAttorney) {
            profileDocumentsMachineReadableAttorney.push(profile?.documents[i])
        } else {
            profileDocuments.push(profile?.documents[i])
        }
    }

    return (
        <div className={style.container}>
            {loading ? (
                <CenteredSpin />
            ) : (
                <>
                    <div className={style.inner}>
                        <div className={style.header}>
                            <div className={style.titleWrapper}>
                                <span
                                    className={style.backLink}
                                    onClick={() => navigate(isUserAdmin ? '/questionnaire-approval' : -1)}
                                >
                                    <ArrowLeft />
                                </span>
                                <div>
                                    <h1 className={style.title}>
                                        {action === EAction.SignUp
                                            ? 'Согласование Анкеты '
                                            : action === EAction.ChangeRole
                                            ? 'Согласование смены роли '
                                            : action === EAction.Edit
                                            ? 'Согласование редактирования профиля '
                                            : ''}
                                        {profile?.user_type === 0
                                            ? `${profile?.last_name} ${profile?.first_name} ${profile?.middle_name}`
                                            : profile?.company_name}
                                    </h1>
                                </div>
                                {profile?.profile_status ? (
                                    <Tag
                                        outline
                                        transparent
                                        color={
                                            profile?.profile_status === EStatus.Rejected
                                                ? 'red'
                                                : profile?.profile_status === EStatus.Agreed
                                                ? 'blue'
                                                : undefined
                                        }
                                    >
                                        {statusMap.get(profile?.profile_status)}
                                    </Tag>
                                ) : undefined}
                            </div>
                            <IconButtonSmall icon={<BookSaved />} onClick={openHistory}>
                                История изменений
                            </IconButtonSmall>
                        </div>
                        {prevProfile && showPrevProfile && (
                            <Collapse className={style.oldDataWrapper}>
                                <CardPanel header="Старые данные" key="1">
                                    <Questionnaire
                                        config={getConfig(prevProfile, profile)}
                                        className={style.commonInfo}
                                    />
                                    <div className={style.verticalLine} />
                                    <Sign value={prevProfile} requestId={prevProfile?.request_id} certificateData={prevProfile?.certificate_data} />
                                    {!!prevProfileDocuments.length && (
                                        <>
                                            <div className={style.verticalLine} />
                                            <div className={style.profileHeader}>
                                                {t('documents.docs')}
                                            </div>
                                            <DocumentsGrid documents={prevProfileDocuments} />
                                        </>
                                    )}
                                    {prevProfile.user_type === EUserType.UrLico
                                        && (
                                            (!!prevAttorney && !!prevProfileDocumentsMachineReadableAttorney.length)
                                            || !!prevProfile?.without_attorney
                                        )
                                        && (
                                            <>
                                                <div className={style.verticalLine}/>
                                                <div className={style.profileHeader}>
                                                    {t('profile.docsMachineReadableAttorney')}
                                                </div>
                                                {!!prevAttorney && !!prevProfileDocumentsMachineReadableAttorney.length
                                                    ? (
                                                        <>
                                                            <DocumentsGrid
                                                                documents={
                                                                    prevProfileDocumentsMachineReadableAttorney
                                                                }
                                                            />
                                                            <AttorneyBlock
                                                                attorney={prevAttorney}
                                                                hasChanges={
                                                                    prevAttorney?.company_name !== attorney?.company_name
                                                                    || prevAttorney?.last_name !== attorney?.last_name
                                                                    || prevAttorney?.first_name !== attorney?.first_name
                                                                    || prevAttorney?.middle_name !== attorney?.middle_name
                                                                    || prevAttorney?.date_issue !== attorney?.date_issue
                                                                    || prevAttorney?.validity_period !== attorney?.validity_period
                                                                    || prevAttorney?.number !== attorney?.number
                                                                    || prevAttorney?.internal_number !== attorney?.internal_number
                                                                    || prevAttorney?.company_inn !== attorney?.company_inn
                                                                    || prevAttorney?.natural_person_inn !== attorney?.natural_person_inn
                                                                }
                                                            />
                                                        </>
                                                    ) : (
                                                        <Checkbox checked={prevProfile?.without_attorney} disabled={true}>
                                                            <span className={
                                                                prevProfile?.without_attorney !== profile?.without_attorney
                                                                    ? style.questionnaireRedText
                                                                    : undefined
                                                            }>
                                                                {t('profile.withoutAttorney')}
                                                             </span>
                                                        </Checkbox>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                    {!!prevProfileDocumentsContractMaintainRegister.length && (
                                        <>
                                            <div className={style.verticalLine} />
                                            <div className={style.profileHeader}>
                                                {t('documents.docsContractMaintainRegister')}
                                            </div>
                                            <DocumentsGrid
                                                documents={
                                                    prevProfileDocumentsContractMaintainRegister
                                                }
                                            />
                                        </>
                                    )}
                                </CardPanel>
                            </Collapse>
                        )}
                        <Card className={style.questionnaireCard}>
                            {(profile?.profile_status as EStatus) === EStatus.Rejected && (
                                <div className={style.statusNotAgreedContainer}>
                                    <div className={style.statusText}>
                                        Данные не приняты. Причина:
                                    </div>
                                    <div className={style.statusReason}>
                                        {profile?.profile_comment}
                                    </div>
                                </div>
                            )}
                            {profile?.profile_status === EStatus.UnderRevision && (
                                <div className={style.statusUnderRevisionContainer}>
                                    <div className={style.statusText}>
                                        Данные отправлены на доработку
                                    </div>
                                    <div className={style.statusReason}>
                                        {profile?.profile_comment}
                                    </div>
                                    {/* <Documents /> */}
                                </div>
                            )}
                            {/* {profile?.profile_status === EStatus.Agreed && (
                                <Alert
                                    className={style.agreedAlert}
                                    message="Согласована 20.04.2022 18:35. Оператор: Константинопольский К.В."
                                    type="info"
                                    showIcon
                                />
                            )} */}

                            <div className={style.newDataTitle}>Новые данные</div>
                            {profile && (
                                <Questionnaire
                                    config={getConfig(profile)}
                                    className={style.commonInfo}
                                />
                            )}
                            <div className={style.verticalLine} />
                            <Sign
                                requestId={profile?.request_id}
                                certificateData={profile?.certificate_data}
                                value={profile}
                            />
                            {!!profileDocuments.length && (
                                <>
                                    <div className={style.verticalLine} />
                                    <div className={style.profileHeader}>{t('documents.docs')}</div>
                                    <DocumentsGrid documents={profileDocuments} />
                                </>
                            )}
                            {profile.user_type === EUserType.UrLico
                                && (
                                    (!!attorney && !!profileDocumentsMachineReadableAttorney.length)
                                    || !!profile?.without_attorney
                                )
                                && (
                                    <>
                                        <div className={style.verticalLine}/>
                                        <div className={style.profileHeader}>{t('profile.docsMachineReadableAttorney')}</div>
                                        {!!attorney && !!profileDocumentsMachineReadableAttorney.length
                                            ? (
                                                <>
                                                    <DocumentsGrid documents={profileDocumentsMachineReadableAttorney}/>
                                                    <AttorneyBlock attorney={attorney}/>
                                                </>
                                            )
                                            : (
                                                <Checkbox checked={profile?.without_attorney} disabled={true}>
                                                    {t('profile.withoutAttorney')}
                                                </Checkbox>
                                            )
                                        }
                                    </>
                                )
                            }
                            {!!profileDocumentsContractMaintainRegister.length && (
                                <>
                                    <div className={style.verticalLine} />
                                    <div className={style.profileHeader}>
                                        {t('documents.docsContractMaintainRegister')}
                                    </div>
                                    <DocumentsGrid
                                        documents={profileDocumentsContractMaintainRegister}
                                    />
                                </>
                            )}
                            {(profile?.profile_status === EStatus.New ||
                                profile?.profile_status === EStatus.UnderConsideration) && isUserAdmin && (
                                <>
                                    <div className={style.verticalLine} />
                                    <ButtonContainer
                                        profile={profile}
                                        action={action}
                                    />
                                </>
                            )}
                        </Card>
                    </div>
                    <HistoryModal
                        isModalVisible={isHistoryVisible}
                        onClose={closeHistory}
                        history={history}
                        uid={uid}
                        setIsModalVisible={setIsHistoryVisible}
                    />
                </>
            )}
        </div>
    )
}

export default QuestionnaireApprovalItem
